import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Rice the World · standalone store · at-cost grocery
 * Buy direct from wholesalers · sell online · hand delivery to the existing rails
 * Staffed by the Brotherhood of Misfits · zero profit kept
 */
export default function RiceTheWorldPage() {
  const [manifest, setManifest] = useState(null);
  const [till, setTill] = useState(null);
  const [brotherhood, setBrotherhood] = useState(null);
  const [beatPolicy, setBeatPolicy] = useState(null);
  const [beatTiers, setBeatTiers] = useState(null);
  const [beatState, setBeatState] = useState("");
  const [beatAdults, setBeatAdults] = useState(1);
  const [beatChildren, setBeatChildren] = useState(0);
  const [beatQuote, setBeatQuote] = useState(null);
  const [pilotProgress, setPilotProgress] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/sage/rice/manifest`).then(r => r.json()).then(setManifest).catch(() => {});
    fetch(`${API}/api/sage/rice/till`).then(r => r.json()).then(setTill).catch(() => {});
    fetch(`${API}/api/sage/brotherhood/tally`).then(r => r.json()).then(setBrotherhood).catch(() => {});
    fetch(`${API}/api/sage/rice/state-beat/policy`).then(r => r.json()).then(setBeatPolicy).catch(() => {});
    fetch(`${API}/api/sage/rice/state-beat/tiers`).then(r => r.json()).then(setBeatTiers).catch(() => {});
    fetch(`${API}/api/sage/rice/state-beat/pilot-progress`).then(r => r.json()).then(setPilotProgress).catch(() => {});
  }, []);

  useEffect(() => {
    if (!beatState) { setBeatQuote(null); return; }
    fetch(`${API}/api/sage/rice/state-beat/quote?state=${encodeURIComponent(beatState)}&adults=${beatAdults}&children=${beatChildren}`)
      .then(r => r.json()).then(setBeatQuote).catch(() => setBeatQuote(null));
  }, [beatState, beatAdults, beatChildren]);

  return (
    <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e]" data-testid="rice-the-world-page">
      {/* MODEL BUILD IN PROGRESS banner — Founder ruling 2026-08-11 */}
      <div className="bg-[#c8b78a] text-[#1e1e1e] py-4 px-6 text-center" data-testid="rice-status-banner">
        <p className="text-2xl md:text-3xl font-serif font-bold uppercase tracking-wider">
          MODEL BUILD IN PROGRESS
        </p>
        <p className="text-sm font-serif italic mt-1">
          The rails are named. The math is drafted. The Brotherhood is being hired to run it.
        </p>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex items-baseline gap-3 border-b-2 border-[#b58c2a] pb-4">
          <span className="text-4xl font-serif text-[#2c3e2a]">Rice the World</span>
          <span className="text-lg text-[#7c8a75] font-serif italic">a standalone store · not part of any family</span>
        </div>
        <p className="mt-6 text-2xl font-serif text-[#2c3e2a] leading-snug max-w-3xl">
          Wholesale + 20%. Delivered to your door. Zero profit kept.
        </p>
        <p className="mt-3 text-base text-[#4a5a48] font-serif max-w-3xl">
          Every dollar above cost splits three ways, publicly: 60% cost of food and delivery,
          20% wages and running cost — <strong>the Brotherhood of Misfits gets paid first</strong> — and
          20% into the <strong>feeding valve</strong>, the till that pays for the monthly free ration
          and lowers food prices across the store as it grows.
        </p>
      </section>

      {/* Monthly ration */}
      {manifest && (
        <section className="max-w-6xl mx-auto px-6 pb-12" data-testid="ration-card">
          <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
            The monthly free ration (per family)
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(manifest.monthly_free_ration || {}).map(([k, v]) => (
              <div key={k} className="bg-white border border-[#d9d3bd] rounded p-4">
                <div className="text-sm font-serif italic text-[#7c8a75]">{k.replace(/_/g, " ")}</div>
                <div className="text-2xl font-serif text-[#2c3e2a]">{v}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm font-serif text-[#4a5a48]">
            Free. Delivered once per month. Shipped only when the till holds the cost of every approved family's ration.
            No promises the ledger cannot cash.
          </p>
        </section>
      )}

      {/* Till state */}
      {till && (
        <section className="max-w-6xl mx-auto px-6 pb-12" data-testid="till-card">
          <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
            The feeding valve · live
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <TillTile label="till" value={`$${Number(till.till_usd || 0).toFixed(2)}`} testid="till-usd" />
            <TillTile label="inflow to date" value={`$${Number(till.feeding_valve_inflow_usd || 0).toFixed(2)}`} testid="till-inflow" />
            <TillTile label="approved families waiting" value={till.approved_families_awaiting || 0} testid="till-waiting" />
            <TillTile
              label="free ration"
              value={till.free_ration_enabled ? "ON" : "OFF"}
              accent={till.free_ration_enabled ? "#2c3e2a" : "#b58c2a"}
              testid="till-status"
            />
          </div>
          <p className="mt-3 text-sm font-serif italic text-[#4a5a48]">{till.free_ration_status_note}</p>
        </section>
      )}

      {/* Brotherhood credit strip */}
      <section className="max-w-6xl mx-auto px-6 pb-12" data-testid="brotherhood-strip">
        <div className="bg-[#2c3e2a] text-[#fbfaf6] rounded p-6">
          <h2 className="text-2xl font-serif border-l-4 border-[#b58c2a] pl-4 mb-4">
            Staffed by the Brotherhood of Misfits
          </h2>
          <p className="text-base font-serif leading-snug max-w-3xl">
            Every picker, packer, dispatcher, and support voice at Rice the World was hired from the Brotherhood
            of Misfits — the funnel that catches the homeless, the disabled, the formerly incarcerated, the
            long-term unemployed, and any other misfit the market has thrown away, and lifts them into paid,
            dignified work. No résumé bar. No credit check bar. A story and a willingness are enough. The test
            is the work itself.
          </p>
          {brotherhood && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <TillTile label="hires active" value={brotherhood.hires_active || 0} accent="#fbfaf6" darkCard testid="br-hires" />
              <TillTile label="applications pending" value={brotherhood.applications_pending || 0} accent="#fbfaf6" darkCard testid="br-pending" />
              <TillTile label="hours worked" value={Number(brotherhood.hours_worked_total || 0).toFixed(1)} accent="#fbfaf6" darkCard testid="br-hours" />
              <TillTile label="wages paid" value={`$${Number(brotherhood.wages_paid_usd_total || 0).toFixed(2)}`} accent="#fbfaf6" darkCard testid="br-wages" />
            </div>
          )}
          <a
            href="/brotherhood"
            className="inline-block mt-6 border border-[#b58c2a] text-[#b58c2a] py-2 px-5 rounded font-serif hover:bg-[#b58c2a] hover:text-[#2c3e2a] transition"
            data-testid="brotherhood-apply-link"
          >
            Apply to the Brotherhood →
          </a>
        </div>
      </section>

      {/* ── STATE-BEAT LEDGER · Founder ruling 2026-08-21 ─────────── */}
      {beatPolicy && (
        <section className="max-w-6xl mx-auto px-6 pb-12" data-testid="state-beat-section">
          <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-2" data-testid="state-beat-headline">
            The State-Beat Ledger
          </h2>
          <p className="text-sm font-serif italic text-[#7c8a75] mb-4">
            Rice's SNAP replacement · <strong>Rice's monthly benefit = your state's SNAP maximum + $10</strong> · at every tier
          </p>

          {/* MO Till Meter · Founder ruling 2026-08-22 */}
          {pilotProgress && (
            <div className="bg-[#2c3e2a] text-[#fbfaf6] rounded p-5 mb-6" data-testid="mo-till-meter">
              <div className="flex justify-between items-baseline mb-2">
                <div>
                  <div className="text-xs font-serif italic text-[#c8b78a] uppercase tracking-wide">
                    Pilot state · {pilotProgress.pilot_state_code}
                  </div>
                  <div className="text-lg font-serif" data-testid="mo-till-status">
                    {pilotProgress.door_status}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-serif" data-testid="mo-till-pct">
                    {pilotProgress.progress_pct.toFixed(1)}%
                  </div>
                  <div className="text-xs font-serif italic text-[#c8b78a]">funded</div>
                </div>
              </div>
              {/* Progress bar */}
              <div className="w-full h-3 bg-[#1a2418] rounded overflow-hidden mb-3" data-testid="mo-till-bar-track">
                <div
                  className="h-full bg-[#b58c2a] transition-all duration-500"
                  style={{ width: `${Math.min(100, pilotProgress.progress_pct)}%` }}
                  data-testid="mo-till-bar-fill"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-serif">
                <div>
                  <div className="text-[#c8b78a] italic uppercase tracking-wide">Till today</div>
                  <div className="text-lg" data-testid="mo-till-current">
                    ${Number(pilotProgress.till_usd).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-[#c8b78a] italic uppercase tracking-wide">Opening-month target</div>
                  <div className="text-lg" data-testid="mo-till-target">
                    ${Number(pilotProgress.projected_monthly_obligation_usd).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-[#c8b78a] italic uppercase tracking-wide">Households planned</div>
                  <div className="text-lg">
                    {pilotProgress.obligation_households_used}
                  </div>
                </div>
                <div>
                  <div className="text-[#c8b78a] italic uppercase tracking-wide">Per-household beat</div>
                  <div className="text-lg">
                    ${pilotProgress.avg_rice_beat_per_household_usd}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs font-serif italic text-[#c8b78a]" data-testid="mo-till-note">
                {pilotProgress.note}
              </p>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white border border-[#d9d3bd] rounded p-5" data-testid="state-beat-phase-1">
              <div className="text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide mb-1">Phase 1 · live pilot</div>
              <div className="text-lg font-serif text-[#2c3e2a] mb-2">Flat per household</div>
              <p className="text-sm font-serif text-[#4a5a48]">
                One flat number per household, per state (state SNAP max + $10). Keeps the pilot administrable and the math checkable in public.
              </p>
            </div>
            <div className="bg-white border border-[#d9d3bd] rounded p-5" data-testid="state-beat-phase-2">
              <div className="text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide mb-1">Phase 2 · planned</div>
              <div className="text-lg font-serif text-[#2c3e2a] mb-2">Per adult 18+ · children as dependents</div>
              <p className="text-sm font-serif text-[#4a5a48]">
                Each adult 18+ raises the household's payment tier. Children raise the ration quantity but are counted as people, not monetary receivers.
              </p>
            </div>
          </div>

          {/* Eligible-items policy */}
          <div className="bg-[#f6f1e3] border border-[#d9d3bd] rounded p-5 mb-6" data-testid="state-beat-policy-card">
            <div className="text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide mb-2">What Rice's beat covers · policy</div>
            <div className="grid md:grid-cols-2 gap-4 text-sm font-serif text-[#4a5a48]">
              <div>
                <div className="font-bold text-[#2c3e2a] mb-1">Included</div>
                <ul className="list-disc list-inside space-y-1">
                  {(beatPolicy.eligible_items?.food_categories_included || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-bold text-[#2c3e2a] mb-1">Excluded</div>
                <ul className="list-disc list-inside space-y-1">
                  {(beatPolicy.eligible_items?.excluded || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
                {beatPolicy.eligible_items?.hot_prepared_food_exceptions && (
                  <div className="mt-3 border-t border-[#d9d3bd] pt-3" data-testid="state-beat-fastfood-trio">
                    <div className="font-bold text-[#2c3e2a] mb-2">
                      Fast-food exceptions · the top three
                    </div>
                    <ul className="space-y-2">
                      {beatPolicy.eligible_items.hot_prepared_food_exceptions.map((c, i) => (
                        <li key={i}>
                          <div className="font-bold text-[#2c3e2a]">{c.chain}</div>
                          <p className="italic text-xs">{c.why}</p>
                          {c.founder_note && (
                            <p className="text-xs text-[#b58c2a] italic mt-1">"{c.founder_note}"</p>
                          )}
                        </li>
                      ))}
                    </ul>
                    {beatPolicy.eligible_items.hot_prepared_food_exception_note && (
                      <p className="italic text-xs mt-2 text-[#7c8a75]">
                        {beatPolicy.eligible_items.hot_prepared_food_exception_note}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 text-xs font-serif italic text-[#7c8a75]">
              Posture: <strong>{beatPolicy.posture}</strong>
              {beatPolicy.pilot_state_code && (
                <span data-testid="state-beat-pilot-flag" className="ml-3">
                  · Pilot state: <strong>{beatPolicy.pilot_state_code}</strong>
                  {beatPolicy.pilot_state_note && (
                    <span> — {beatPolicy.pilot_state_note}</span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Side-by-side quote calculator */}
          <div className="bg-white border-2 border-[#b58c2a] rounded p-5" data-testid="state-beat-quote-card">
            <div className="text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide mb-3">See your state · side by side</div>
            <div className="grid md:grid-cols-4 gap-3 mb-4">
              <label className="block">
                <div className="text-xs font-serif text-[#4a5a48] mb-1">State</div>
                <select
                  data-testid="state-beat-state-select"
                  value={beatState}
                  onChange={(e) => setBeatState(e.target.value)}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif text-[#2c3e2a] bg-white"
                >
                  <option value="">Pick a state…</option>
                  {(beatTiers?.rows || []).map((r) => (
                    <option key={r.state_key} value={r.state_key}>
                      {r.state_code}{r.state_key !== r.state_code ? ` · ${r.state_key}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className="text-xs font-serif text-[#4a5a48] mb-1">Adults (18+)</div>
                <input
                  data-testid="state-beat-adults"
                  type="number" min={1} max={12}
                  value={beatAdults}
                  onChange={(e) => setBeatAdults(Math.max(1, parseInt(e.target.value || "1", 10)))}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif text-[#2c3e2a] bg-white"
                />
              </label>
              <label className="block">
                <div className="text-xs font-serif text-[#4a5a48] mb-1">Children (under 18)</div>
                <input
                  data-testid="state-beat-children"
                  type="number" min={0} max={12}
                  value={beatChildren}
                  onChange={(e) => setBeatChildren(Math.max(0, parseInt(e.target.value || "0", 10)))}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif text-[#2c3e2a] bg-white"
                />
              </label>
              <div className="flex items-end">
                <div className="text-xs font-serif italic text-[#7c8a75]">Household size ·
                  <strong className="text-[#2c3e2a]"> {beatAdults + beatChildren}</strong>
                </div>
              </div>
            </div>

            {beatQuote && !beatQuote.detail && (
              <div className="grid md:grid-cols-3 gap-3" data-testid="state-beat-comparison">
                <div className="bg-[#f6f1e3] border border-[#d9d3bd] rounded p-4">
                  <div className="text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide">Your state pays</div>
                  <div className="text-3xl font-serif text-[#2c3e2a] mt-1" data-testid="state-beat-state-amount">
                    ${beatQuote.state_snap_max_usd}
                  </div>
                  <div className="text-xs font-serif italic text-[#7c8a75] mt-1">SNAP max · per month</div>
                </div>
                <div className="bg-[#e8f0e6] border-2 border-[#2c3e2a] rounded p-4">
                  <div className="text-xs font-serif italic text-[#2c3e2a] uppercase tracking-wide">Rice pays</div>
                  <div className="text-3xl font-serif text-[#2c3e2a] mt-1" data-testid="state-beat-rice-amount">
                    ${beatQuote.rice_beat_usd}
                  </div>
                  <div className="text-xs font-serif italic text-[#2c3e2a] mt-1"><strong>+ $10 over</strong> · per month</div>
                </div>
                <div className="bg-[#2c3e2a] text-[#fbfaf6] rounded p-4">
                  <div className="text-xs font-serif italic text-[#c8b78a] uppercase tracking-wide">Difference</div>
                  <div className="text-3xl font-serif mt-1" data-testid="state-beat-diff-amount">
                    + ${beatQuote.beat_over_state_usd}
                  </div>
                  <div className="text-xs font-serif italic text-[#c8b78a] mt-1">Every household · every state · every tier</div>
                </div>
              </div>
            )}

            <div className="mt-4 text-xs font-serif italic text-[#7c8a75]">
              Source: USDA FY2026 Thrifty Food Plan · seed values <strong>unverified</strong> · applicant must confirm against the current state figure.
              The door opens for a state ONLY when Rice's till holds the projected next-month obligation for every enrolled household in that state.
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-6 py-8 border-t border-[#d9d3bd]">
        <p className="text-sm font-serif italic text-[#7c8a75]">
          Rice the World is a standalone store. It is not owned by, controlled by, or a subsidiary of any other
          house. Its till, its ledger, and its wages all stand alone. Glass to the grain of rice.
        </p>
      </footer>
    </div>
  );
}

function TillTile({ label, value, accent = "#2c3e2a", darkCard = false, testid }) {
  return (
    <div
      className={darkCard ? "bg-[#3a4c38] border border-[#4a5c48] rounded p-4" : "bg-white border border-[#d9d3bd] rounded p-4"}
      data-testid={testid}
    >
      <div className={darkCard ? "text-xs font-serif italic text-[#c8b78a] uppercase tracking-wide" : "text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide"}>
        {label}
      </div>
      <div className="text-2xl font-serif mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
